import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Loader from "../../atoms/Loader/Loader";
import { BoardIconButton } from "../../atoms/BoardDesignSystem";
import { BOARD_Z_INDEX } from "../../../app/utils/projectBoard/boardDesignTokens";
import {
  buildLargePreviewUrl,
  canPreviewBoardAttachment,
  downloadBoardAttachmentBlob,
  fetchBoardAttachmentBlob,
  isImageAttachment,
  isPdfAttachment,
} from "../../../app/utils/projectBoard/projectBoardAttachment";
import { showErrorToast } from "../../../app/utils/common/Toast";
import "./BoardAttachmentViewer.scss";

const BoardAttachmentViewer = ({ attachment, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [blob, setBlob] = useState(null);
  const [objectUrl, setObjectUrl] = useState("");
  const [fallbackImageUrl, setFallbackImageUrl] = useState("");

  const fileName = attachment?.fileName || "Attachment";
  const fileType = attachment?.fileType || "";
  const cardId = attachment?.cardId;
  const attachmentId = attachment?.attachmentId;

  const loadAttachment = useCallback(async () => {
    if (!cardId || !attachmentId) {
      showErrorToast("Invalid attachment.");
      onClose?.();
      return;
    }

    setLoading(true);
    setBlob(null);
    setFallbackImageUrl("");
    setObjectUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });

    try {
      const nextBlob = await fetchBoardAttachmentBlob(cardId, attachmentId);
      setBlob(nextBlob);
      setObjectUrl(URL.createObjectURL(nextBlob));
    } catch (error) {
      const largePreview = buildLargePreviewUrl(attachment?.thumbnailUrl);
      if (isImageAttachment(fileType) && largePreview) {
        setFallbackImageUrl(largePreview);
        return;
      }
      showErrorToast(error?.message || "Could not open attachment.");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [attachment?.thumbnailUrl, attachmentId, cardId, fileType, onClose]);

  useEffect(() => {
    loadAttachment();
  }, [loadAttachment]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl]
  );

  if (!attachment) return null;

  const handleDownload = async () => {
    if (blob) {
      downloadBoardAttachmentBlob(blob, fileName);
      return;
    }
    if (fallbackImageUrl) {
      try {
        const response = await fetch(fallbackImageUrl);
        const imageBlob = await response.blob();
        downloadBoardAttachmentBlob(imageBlob, fileName);
      } catch {
        showErrorToast("Could not download attachment.");
      }
    }
  };

  const showImage = !loading && (objectUrl || fallbackImageUrl) && isImageAttachment(fileType);
  const showPdf = !loading && objectUrl && isPdfAttachment(fileType);
  const showFallback =
    !loading && objectUrl && !canPreviewBoardAttachment(fileType);

  return createPortal(
    <div
      className="board-attachment-viewer"
      style={{ zIndex: BOARD_Z_INDEX.modal + 20 }}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${fileName}`}
      onClick={onClose}
    >
      <div
        className="board-attachment-viewer__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="board-attachment-viewer__header">
          <h3 className="board-attachment-viewer__title">{fileName}</h3>
          <div className="board-attachment-viewer__actions">
            {!loading && (blob || fallbackImageUrl) && (
              <button
                type="button"
                className="board-attachment-viewer__download"
                onClick={handleDownload}
              >
                Download
              </button>
            )}
            <BoardIconButton label="Close preview" icon="×" onClick={onClose} />
          </div>
        </header>

        <div className="board-attachment-viewer__body">
          {loading && (
            <div className="board-attachment-viewer__loading">
              <Loader />
            </div>
          )}

          {showImage && (
            <img
              src={objectUrl || fallbackImageUrl}
              alt={fileName}
              className="board-attachment-viewer__image"
            />
          )}

          {showPdf && (
            <iframe
              title={fileName}
              src={objectUrl}
              className="board-attachment-viewer__pdf"
            />
          )}

          {showFallback && (
            <div className="board-attachment-viewer__fallback">
              <p>Preview is not available for this file type.</p>
              <button type="button" onClick={handleDownload}>
                Download {fileName}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BoardAttachmentViewer;
